import React, { useRef, useEffect, useMemo } from 'react';
import { Virtuoso } from 'react-virtuoso';
import CardHeader from '@material-ui/core/CardHeader';
import Avatar from '@material-ui/core/Avatar';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import {
  bindMenu,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import Box from '@material-ui/core/Box';
import Menu from '@material-ui/core/Menu';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import MuiLink from '@material-ui/core/Link';
import ArchiveIcon from '@material-ui/icons/Archive';
import CardContent from '@material-ui/core/CardContent';
import AvatarGroup from '@material-ui/lab/AvatarGroup';
import Message from '../Message';
import CardActions from '@material-ui/core/CardActions';
import Composer from '../Composer';
import Card from '@material-ui/core/Card';
import Alert from '@material-ui/lab/Alert';
import { useMutation, useSubscription } from '@apollo/react-hooks';
import Spin from 'components/common/Spin';
import {
  GET_CONVO_DETAILS,
  ADD_MESSAGE,
  GET_CONVOS,
  UPDATE_CONVO,
  UPDATE_USER_CONVO_STATUS,
} from 'graphql/conversations';
import { withStyles } from '@material-ui/core/styles';
import MenuItem from '@material-ui/core/MenuItem';
import useSnack from 'hooks/useSnack';
import stc from 'string-to-color';
import htmlToText from 'html-to-text';
import moment from 'moment';
import qs from 'querystring';
import { getFullName, getInitials } from 'utils';
import { USER_TYPES } from 'constant/userTypes';
import withConfirm from 'hooks/withConfirm';
import DateDivider from '../DateDivider';
import AddUsersModal from './AddUsersModal';
import useStyles from '../../styles';

import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { List } from '@material-ui/core';

const StyledMenuItem = withStyles((theme) => ({
  root: {
    '&:focus': {
      'backgroundColor': theme.palette.primary.main,
      '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
        color: theme.palette.common.white,
      },
    },
  },
}))(MenuItem);

const Conversation = ({
  confirm,
  match: {
    params: { id },
  },
  history,
}) => {
  const classes = useStyles();
  const user = useSelector((s) => s.user.user);
  const snack = useSnack();
  const listRef = useRef();
  const popupState = usePopupState({ variant: 'popover', popupId: 'chatMenu' });
  const { data, loading, error } = useSubscription(GET_CONVO_DETAILS, {
    variables: { id },
  });

  const [addMessage] = useMutation(ADD_MESSAGE);
  const [updateConvo] = useMutation(UPDATE_CONVO);
  const [updateStatus] = useMutation(UPDATE_USER_CONVO_STATUS, {
    variables: { convoId: id, userId: user.id, timestamp: 'now()' },
  });

  // const Components = useMemo(() => {
  //   return {
  //     List: React.forwardRef(({ style, children }, ref) => {
  //       return (
  //         <CardContent ref={ref}>
  //           {children}
  //         </CardContent>
  //       );
  //     }),
  //   };
  // }, []);

  useEffect(() => {
    if (!loading && data) {
      const query = qs.parse(window.location.search.substr(1));
      if (query.mid && type === 'sms') {
        setTimeout(() => {
          listRef.current.scrollToIndex({
            index: data.convo.messages.length,
          });
        }, 100);
      }
    }
  }, [loading]);

  useEffect(() => {
    if (listRef.current && type === 'sms' && data) {
      listRef.current.scrollToIndex({
        index: data.convo.messages.length,
      });
    }
  }, [data]);

  useEffect(() => {
    updateStatus();
  }, [data]);

  const sendMessage = async (message, recipientIds, attachments) => {
    try {
      const recipients = recipientIds && {
        data: recipientIds.map((id) => ({ user_id: id })),
      };

      return await addMessage({
        variables: {
          data: {
            type: data.convo.type,
            convo_id: id,
            html: message,
            text: htmlToText.fromString(message, {
              singleNewLineParagraphs: true,
            }),
            created_by: user.id,
            recipients,
            attachments,
          },
        },
      });
    } catch (e) {
      snack.error(e.message);
    }
  };

  const onArchive = () => {
    popupState.close();
    confirm(async () => {
      try {
        await updateConvo({
          variables: {
            id,
            input: {
              archived: true,
            },
          },
        });
        snack.success('Conversation archived');
        history.replace('/inbox/' + data.convo.type);
      } catch (e) {
        snack.error(e.message);
      }
    });
  };

  if (error) {
    return (
      <Alert variant="filled" color={'error'}>
        {error.message}
      </Alert>
    );
  }
  if (loading) return <Spin spinning />;

  const { convo } = data;
  if (!convo) {
    return (
      <Alert color="error" variant="filled">
        Conversation not found.
      </Alert>
    );
  }

  const { users, student, group, type } = convo;

  const renderTitle = () => {
    if (type === 'sms') {
      if (group) return group.name;
      return getFullName(convo.user) + ' - ' + USER_TYPES[convo.user.user_type];
    }
    return convo.subject;
  };

  const renderSubHeader = () => {
    if (type === 'sms' && student) {
      if (convo.user.user_type === 'parent') {
        if (!convo.user.guardian_children.length) return null;
        return (
          <Box>
            Students:&nbsp;
            {convo.user.guardian_children.map((i) => (
              <MuiLink
                component={Link}
                style={{ marginRight: 8 }}
                to={`/inbox/sms/${i.student.user_cognito_sub}`}
              >
                {getFullName(i.student)}
              </MuiLink>
            ))}
          </Box>
        );
      } else {
        if (!convo.user.student_guardians.length) return null;
        return (
          <Box>
            Parents:&nbsp;
            {convo.user.student_guardians.map((i) => (
              <MuiLink
                component={Link}
                style={{ marginRight: 8 }}
                to={`/inbox/sms/${i.guardian.user_cognito_sub}`}
              >
                {getFullName(i.guardian)}
              </MuiLink>
            ))}
          </Box>
        );
      }
    } else if (type === 'email') {
      return (
        <Box display="flex">
          <Box marginRight={1}>
            {users.map(({ user }) => getFullName(user)).join(', ')}
          </Box>
          <AddUsersModal
            users={users}
            student={student}
            convoId={id}
            type={type}
          />
        </Box>
      );
    }
    return null;
  };

  const renderAvatar = () => {
    if (type === 'sms') {
      if (group) {
        return (
          <Avatar style={{ backgroundColor: stc(group.name) }}>
            {getInitials(group.name)}
          </Avatar>
        );
      }
      if (convo.user) {
        const avatarEl = (
          <Avatar
            style={{ backgroundColor: stc(convo.user.user_email) }}
            src={convo.user.avatar}
          >
            {getInitials(convo.user.name, convo.user.user_last_name)}
          </Avatar>
        );

        if (convo.user.user_type === 'student') {
          return (
            <Link to={`/students/${convo.user.user_id}/timeline`}>
              {avatarEl}
            </Link>
          );
        }
        return avatarEl;
      }
    }
    return (
      <AvatarGroup spacing={'small'} className={classes.avatarGroup}>
        {users.map(({ user }) => (
          <Avatar
            src={user.avatar}
            style={{ backgroundColor: stc(user.user_email) }}
          >
            {getInitials(user.user_first_name, user.user_last_name)}
          </Avatar>
        ))}
      </AvatarGroup>
    );
  };

  return (
    <Card className={classes.card}>
      <CardHeader
        className={classes.header}
        avatar={renderAvatar()}
        subheaderTypographyProps={{
          variant: 'caption',
        }}
        title={renderTitle()}
        subheader={renderSubHeader()}
        action={
          <>
            {student && (
              <IconButton {...bindTrigger(popupState)}>
                <MoreVertIcon />
              </IconButton>
            )}
            <Menu {...bindMenu(popupState)}>
              {type !== 'sms' && [
                <StyledMenuItem onClick={onArchive}>
                  <ListItemIcon>
                    <ArchiveIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Archive" />
                </StyledMenuItem>,
              ]}
            </Menu>
          </>
        }
      />
      {type === 'email' ? (
        <CardContent className={classes.messagesContainerEmail} ref={listRef}>
          {convo.messages.map((message, idx) => (
            <Message
              key={message.id}
              message={message}
              user={user}
              isLast={idx === convo.messages.length - 1}
              isFirst={idx === 0}
            />
          ))}
        </CardContent>
      ) : (
        <Virtuoso
          ref={listRef}
          style={{
            height: 'calc(100vh - 270px)',
            background: 'rgb(235 235 235 / 40%)',
            border: '1px solid #dfdfdf'
          }}
          data={convo.messages}
          itemContent={(index, message) => {
            const prev = convo.messages[index - 1];
            if (
              index === 0 ||
              !moment(message.created_at).isSame(prev.created_at, 'day')
            ) {
              return (
                <Box key={message.id}>
                  {index === 0 && (
                    <Typography
                      className={classes.textCenter}
                      color="textSecondary"
                      variant="subtitle2"
                    >
                      This is beginning of the conversation
                    </Typography>
                  )}
                  <DateDivider date={message.created_at} />
                  <Message message={message} user={user} />
                </Box>
              );
            }
            return <Message key={message.id} message={message} user={user} />;
          }}
        />
      )}
      <CardActions className={classes.composerContainer}>
        {type === 'sms' && convo.user && !convo.user.user_phone ? (
          'User has no phone number'
        ) : (
          <Composer
            onSend={sendMessage}
            type={type}
            student={student}
            user={user}
            convoId={id}
          />
        )}
      </CardActions>
    </Card>
  );
};

export default withConfirm(Conversation);
